/**
 * Privacy Policy — attorney-approved copy (Effective Date: 2026-05-23).
 * Structure mirrors Terms.jsx and Refunds.jsx for a cohesive policies
 * section.
 */
export default function Privacy() {
  return (
    <div className="max-w-[820px] mx-auto px-6 lg:px-12 py-14 lg:py-20" data-testid="privacy-page">
      <p className="overline mb-4">Policy</p>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-6">Privacy Policy.</h1>
      <p className="text-[13px] font-mono uppercase tracking-[0.22em] text-stone mb-10">
        Effective Date: May 23, 2026
      </p>
      <div className="hairline mb-10" />

      <div className="text-charcoal text-[16px] md:text-[17px] leading-[1.75] space-y-6">
        <p>
          BULKBUYKITS.COM/RESEARCH 1617 (&ldquo;BULKBUYKITS.COM/RESEARCH 1617,&rdquo; &ldquo;we,&rdquo;
          &ldquo;our,&rdquo; or &ldquo;us&rdquo;) respects your privacy and is committed to protecting
          your personal information. This Privacy Policy outlines how we collect, use, share, and
          safeguard your data when you interact with our website at BULKBUYKITS.COM.
        </p>

        <Section title="1. Information We Collect">
          <SubHead>Personal Information You Provide</SubHead>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Name, email address, mobile telephone number</li>
            <li>Billing and shipping addresses</li>
            <li>Payment information (processed securely by third-party payment processors; we do not store full payment card numbers)</li>
            <li>Account credentials (username, password)</li>
            <li>Research affiliation information (if provided)</li>
            <li>Communications you send to us</li>
          </ul>
          <SubHead>Information Collected Automatically</SubHead>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Browser type and version, operating system, device type</li>
            <li>IP address and approximate geographic location</li>
            <li>Pages visited, time spent on pages, navigation paths</li>
            <li>Referring URLs and search terms used to find our website</li>
            <li>Click and scroll behavior (via analytics tools)</li>
          </ul>
          <SubHead>Information from Third Parties</SubHead>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Payment verification data from payment processors</li>
            <li>Shipping confirmation data from carriers</li>
            <li>Marketing attribution data from advertising platforms</li>
          </ul>
        </Section>

        <Section title="2. How We Use Your Information">
          <p>We use collected information to:</p>
          <ol className="list-[lower-alpha] pl-6 space-y-1.5">
            <li>Process and fulfill your orders;</li>
            <li>Communicate with you regarding orders, account activity, shipping, and customer support;</li>
            <li>Send marketing communications (where you have opted in);</li>
            <li>Verify your identity and prevent fraud;</li>
            <li>Improve website functionality, user experience, and product offerings;</li>
            <li>Conduct analytics to understand customer behavior and inform business decisions;</li>
            <li>Comply with applicable laws, regulations, and legal processes;</li>
            <li>Enforce our Terms &amp; Conditions and protect our legal rights.</li>
          </ol>
        </Section>

        <Section title="3. Legal Basis for Processing">
          <p>We process your personal information based on:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Performance of a contract (to fulfill your orders)</li>
            <li>Your consent (for marketing communications)</li>
            <li>Our legitimate interests (fraud prevention, business operations, analytics)</li>
            <li>Legal obligations (tax, regulatory, and compliance requirements)</li>
          </ul>
        </Section>

        <Section title="4. Data Protection & Security">
          <p>
            All payment transactions are processed securely by PCI-DSS-compliant third-party payment
            processors. BULKBUYKITS.COM/RESEARCH 1617 does not store full payment card numbers, CVV
            codes, or bank account numbers. We implement reasonable technical and organizational
            measures to protect your personal data from unauthorized access, loss, alteration, or
            misuse, including:
          </p>
          <ol className="list-[lower-alpha] pl-6 space-y-1.5">
            <li>Encrypted data transmission (SSL/TLS);</li>
            <li>Access controls limiting employee access to personal data;</li>
            <li>Regular security reviews and updates;</li>
            <li>Secure third-party service providers.</li>
          </ol>
          <p>
            No method of electronic transmission or storage is 100% secure. While we strive to protect
            your information, we cannot guarantee absolute security.
          </p>
        </Section>

        <Section title="5. Data Retention">
          <p>
            We retain your personal information for as long as necessary to fulfill the purposes
            outlined in this Privacy Policy, unless a longer retention period is required or permitted
            by law. Specifically:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Order and transaction records: Retained for seven (7) years for tax, accounting, and regulatory compliance purposes</li>
            <li>Account information: Retained for the duration of your active account, plus two (2) years following account closure</li>
            <li>Marketing communication preferences: Retained until you opt out, plus the period necessary to honor your opt-out request</li>
            <li>Customer support records: Retained for three (3) years from the date of last contact</li>
            <li>Analytics data: Retained in aggregate, anonymized form for up to twenty-six (26) months</li>
          </ul>
          <p>
            You may request deletion of your personal information at any time, subject to legal
            retention obligations (see Section 9 below).
          </p>
        </Section>

        <Section title="6. Cookies & Tracking Technologies">
          <p>
            We use cookies, web beacons, and similar tracking technologies to enhance user experience
            and monitor site performance.
          </p>
          <p>Categories of cookies used:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Essential cookies:</strong> Required for website functionality (cannot be disabled)</li>
            <li><strong>Analytics cookies:</strong> Help us understand how visitors use our site (Google Analytics 4)</li>
            <li><strong>Marketing cookies:</strong> Used to deliver and measure advertising effectiveness (Google Ads, Meta Pixel where applicable)</li>
          </ul>
          <p>
            You can disable cookies through your browser settings. Disabling certain cookies may affect
            website functionality and your ability to complete purchases.
          </p>
        </Section>

        <Section title="7. Third-Party Disclosure">
          <p>
            We do not sell, rent, or trade personal information to third parties for their independent
            marketing purposes. Information may be shared with the following categories of trusted
            service providers strictly to operate our business and fulfill your orders:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Payment processors (for transaction processing)</li>
            <li>Shipping carriers (for order delivery)</li>
            <li>Email and SMS service providers (for transactional and marketing communications)</li>
            <li>Analytics providers (Google Analytics, similar)</li>
            <li>Advertising platforms (Google Ads, Microsoft Ads, Meta where applicable, for attribution and remarketing)</li>
            <li>Cloud hosting and security providers</li>
            <li>Legal and compliance advisors</li>
          </ul>
          <p>
            We may also disclose your information when required by law, court order, or government
            request, or to protect our rights, property, or safety, or that of our users or others.
          </p>
        </Section>

        <Section title="8. Marketing Communications">
          <p>
            <strong>Email Marketing.</strong> If you have provided your email address and opted in, you
            may receive marketing emails from BULKBUYKITS.COM/RESEARCH 1617. You can unsubscribe at any
            time by clicking the unsubscribe link in any email or contacting support@BULKBUYKITS.COM.
            BULKBUYKITS.COM/RESEARCH 1617 complies with the CAN-SPAM Act and similar applicable laws.
          </p>
          <p>
            <strong>SMS Communications.</strong> BULKBUYKITS.COM/RESEARCH 1617 operates an SMS
            messaging program governed by separate SMS Communications Program Terms (see{" "}
            <a href="/policies/terms" className="text-oxblood hover:text-charcoal underline">
              Terms &amp; Conditions
            </a>
            ). By providing your mobile telephone number and opting in, you consent to receive
            recurring automated marketing and transactional text messages.
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Information Collected Through SMS Program:</strong> Mobile telephone number, consent timestamp and source, message engagement data (delivery status, opt-out events).</li>
            <li><strong>Purpose of SMS Messages:</strong> Order confirmations, shipping notifications, restock alerts, promotional offers, and research community communications.</li>
            <li><strong>Message Frequency:</strong> Up to fifteen (15) messages per month.</li>
            <li><strong>Opt-Out:</strong> Reply STOP to any message to unsubscribe. You will receive a single confirmation message. Transactional messages related to active orders may continue.</li>
            <li><strong>Help:</strong> Reply HELP to any message or contact support@bulkbuykits.com.</li>
            <li><strong>Carrier Charges:</strong> Message and data rates may apply. BULKBUYKITS.COM/RESEARCH 1617 is not responsible for carrier-related delays or charges. Carriers are not liable for delayed or undelivered messages.</li>
            <li><strong>Data Sharing:</strong> Mobile telephone numbers are not sold, rented, or shared with third parties for independent marketing purposes. Numbers are shared only with our SMS platform vendor and shipping carriers to operate the program.</li>
            <li><strong>Eligibility:</strong> The SMS program is available only to individuals located in the United States who are at least twenty-one (21) years of age.</li>
          </ul>
        </Section>

        <Section title="9. Your Privacy Rights">
          <p><strong>All Users.</strong> You have the right to:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Access the personal information we hold about you</li>
            <li>Correct inaccurate personal information</li>
            <li>Request deletion of your personal information (subject to legal retention obligations)</li>
            <li>Opt out of marketing communications</li>
            <li>Withdraw consent where processing is based on consent</li>
          </ul>
          <p>
            To exercise these rights, contact us at{" "}
            <a href="mailto:support@bulkbuykits.com" className="text-oxblood hover:text-charcoal underline">
              support@bulkbuykits.com
            </a>
            . We will respond within fourteen (14) days, or as required by applicable law.
          </p>

          <SubHead>California Residents (CCPA/CPRA)</SubHead>
          <p>
            If you are a California resident, you have additional rights under the California Consumer
            Privacy Act and California Privacy Rights Act:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Right to Know:</strong> Request disclosure of the categories and specific pieces of personal information we have collected about you over the past twelve (12) months</li>
            <li><strong>Right to Delete:</strong> Request deletion of your personal information, subject to legal exceptions</li>
            <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
            <li><strong>Right to Opt Out of Sale or Sharing:</strong> We do not sell personal information. We do not share personal information for cross-context behavioral advertising in a manner requiring opt-out under the CPRA</li>
            <li><strong>Right to Limit Use of Sensitive Personal Information:</strong> We do not use sensitive personal information for purposes beyond those permitted under the CPRA</li>
            <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
          </ul>
          <p>
            To exercise California privacy rights, email support@bulkbuykits.com with the subject line
            &ldquo;California Privacy Request.&rdquo; We may need to verify your identity before
            fulfilling your request. You may also designate an authorized agent to make a request on
            your behalf.
          </p>
          <p>
            <strong>Categories of Personal Information Collected (CCPA Disclosure):</strong> In the
            preceding twelve (12) months, BULKBUYKITS.COM/RESEARCH 1617 has collected the following
            categories of personal information: identifiers (name, email, phone, address, IP address),
            commercial information (purchase history), internet activity (browsing behavior on our
            site), geolocation (approximate location from IP address), and inferences drawn from the
            above (customer preferences).
          </p>

          <SubHead>EU/EEA/UK Residents (GDPR/UK GDPR)</SubHead>
          <p>
            If you are located in the European Union, European Economic Area, or United Kingdom, you
            have additional rights under the General Data Protection Regulation, including the right to
            data portability, the right to object to processing, and the right to lodge a complaint
            with a supervisory authority. Contact support@bulkbuykits.com to exercise these rights.
          </p>
        </Section>

        <Section title="10. Children's Privacy">
          <p>
            BULKBUYKITS.COM/RESEARCH 1617 does not knowingly collect personal information from
            individuals under the age of twenty-one (21). Our website is not directed to children. If
            we become aware that we have collected personal information from a child without verified
            parental consent, we will take steps to delete such information promptly.
          </p>
        </Section>

        <Section title="11. International Data Transfers">
          <p>
            If you access our website from outside the United States, your personal information may be
            transferred to, stored, and processed in the United States. By using our website, you
            consent to such transfer.
          </p>
        </Section>

        <Section title="12. Changes to This Privacy Policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            technology, legal requirements, or other factors. Changes will be effective immediately
            upon posting to this website with an updated effective date. We encourage you to review
            this Privacy Policy periodically.
          </p>
        </Section>

        <Section title="13. Contact Us">
          <p>
            For questions, concerns, or requests regarding this Privacy Policy or your personal
            information, contact:
          </p>
          <address className="not-italic">
            BULKBUYKITS.COM / RESEARCH 1617 &mdash; Privacy Officer<br />
            1617 17th Avenue South<br />
            Nashville, TN 37212<br />
            <a
              href="mailto:support@bulkbuykits.com"
              className="text-oxblood hover:text-charcoal underline"
              data-testid="privacy-support-email"
            >
              support@bulkbuykits.com
            </a>
          </address>
        </Section>

        <div className="hairline my-12" />

        <p className="text-[15px] text-stone">
          Questions? Reach us at{" "}
          <a
            href="mailto:support@bulkbuykits.com"
            className="text-oxblood hover:text-charcoal underline"
            data-testid="privacy-footer-email"
          >
            support@bulkbuykits.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-8 mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function SubHead({ children }) {
  return (
    <h3 className="font-serif text-lg md:text-xl tracking-tight mt-5 mb-2">{children}</h3>
  );
}
